import type { FC } from 'react';

import Modal from '@components/Modal';
import Table from '@components/Table';

import './CapabilityModal.scss';

type CapabilityModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const tableData = {
  cols: [
    {
      key: 'code',
      label: 'O*NET Code',
    },
    {
      key: 'name',
      label: 'O*NET Occupation Name',
    },
    {
      key: 'isco',
      label: 'ISCO code',
    },
    {
      key: 'occupation',
      label: 'ISCO Occupation Name',
    },
  ],
  data: [
    {
      code: '119013',
      name: 'Farmers, Ranchers, and Other Agricultural Managers',
      isco: '1312',
      occupation: 'Aquaculture and Fisheries Production Managers',
    },
    {
      code: '113051',
      name: 'Industrial Production Managers',
      isco: '1321',
      occupation: 'Manufacturing Managers',
    },
    {
      code: '172151',
      name: 'Mining and Geological Engineers, Including Mining Safety Engineers',
      isco: '1322',
      occupation: 'Mining Managers',
    },
    {
      code: '119021',
      name: 'Construction Managers',
      isco: '1323',
      occupation: 'Construction Managers',
    },
    {
      code: '113071',
      name: 'Transportation, Storage, and Distribution Managers',
      isco: '1324',
      occupation: 'Supply, Distribution and Related Managers',
    },
  ],
};

const CapabilityModal: FC<CapabilityModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal
      opened={isOpen}
      title="Methodology"
      onClose={onClose}
      style={{ minWidth: 1200 }}
    >
      <div className="capability-modal">
        <div className="capability-modal__grid">
          <div className="capability-modal__item">
            <div className="capability-modal__subtitle">Step 1</div>
            <div className="capability-modal__arrow">
              Capabilities were derived from O*NET database for each O*NET
            </div>
            <div className="capability-modal__description">
              <b>219 capabilities across 6 categories</b> were collected from
              the O*NET database for all O*NET occupations
            </div>
            <div className="capability-modal__content">
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Ability
                </div>
                <div>Auditory Attention</div>
                <div>Dynamic Flexibility</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Education
                </div>
                <div>Bachelor’s Degree</div>
                <div>High School Diploma</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Knowledge
                </div>
                <div>Biology</div>
                <div>Chemistry</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Skills
                </div>
                <div>Active Listening</div>
                <div>Complex Problem Solving</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Work Activity
                </div>
                <div>Analyzing Data</div>
                <div>Coaching Others</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Work Context
                </div>
                <div>Degree of Automation</div>
                <div>Freedom to make Decisions</div>
              </div>
            </div>
          </div>
          <div className="capability-modal__item">
            <div className="capability-modal__subtitle">Step 2</div>
            <div className="capability-modal__arrow">
              O*NET occupations were mapped to ISCO occupations
            </div>
            <div className="capability-modal__description">
              A total of <b>433 ISCO occupations were mapped</b> at the
              four-digit level
            </div>
            <Table columns={tableData.cols} data={tableData.data} inverted />
          </div>
          <div className="capability-modal__item">
            <div className="capability-modal__subtitle">Step 3</div>
            <div className="capability-modal__arrow">
              Additional capabilities were added by occupation from webscrapping
              and external sources
            </div>
            <div className="capability-modal__description">
              <b>Detailed</b> technical and soft skills with{' '}
              <b>proficiency levels</b> were mapped to refine the 433 capability
              profiles
            </div>
            <div className="capability-modal__content">
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Soft Skills
                </div>
                <div>Cognitive</div>
                <div>Interpersonal</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title"></div>
                <div>Basic</div>
                <div>Basic</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Technical Skills
                </div>
                <div>Analytical</div>
                <div>Software</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title"></div>
                <div>Intermediate</div>
                <div>Advanced</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title">
                  Responsibilities
                </div>
                <div>Autonomy</div>
                <div>Influence</div>
              </div>
              <div className="capability-modal__content-section">
                <div className="capability-modal__content-section-title"></div>
                <div>Advanced</div>
                <div>Advanced</div>
              </div>
            </div>
          </div>
        </div>
        <div className="capability-modal__footer">
          The O*NET framework was used to develop the generic occupation
          capability profiles, which were further refined by the detailed
          capabilities from SFIA and the Singapore skills framework.
        </div>
      </div>
    </Modal>
  );
};

export default CapabilityModal;
